/* eslint-disable react-refresh/only-export-components -- config module exports dialog maps and small local components */
import { ReactNode, useState } from "react";
import { Copy, Check } from "lucide-react";
import { showSuccessToast } from "@/components/custom-toasts/toasts";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";

interface CopyableEmailProps {
  email: string;
  className?: string;
}

function CopyableEmail({ email, className }: CopyableEmailProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      showSuccessToast("Email kopieret til udklipsholderen");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error("Failed to copy email address to the clipboard", {
        operation: "copyTenantDialogEmail",
        error,
      });
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "inline-flex items-center gap-1.5 font-medium text-primary hover:text-primary/80 transition-colors underline",
        className
      )}
    >
      {email}
      {copied ? (
        <Check className="size-3.5 text-success" />
      ) : (
        <Copy className="size-3.5" />
      )}
    </button>
  );
}

export interface TenantDialogConfig {
  title: string;
  description: string;
  customContent?: () => ReactNode;
  onClose?: () => void;
}

/**
 * Mapping from tenant GUIDs to tenant names
 * Add new tenants here by mapping their GUID to a tenant name
 */
export const TENANT_ID_MAP: Record<string, string> = {
  "1e7f55f7-ecf5-4be2-b677-dc75d7ef9deb": "horten", // horten production tenant guid
  //"9493f4d8-5ffa-425b-ad5f-22ae1f8d05c3": "horten", // capacit dev tenant guid
};

/**
 * Get the tenant name from a GUID
 * @param tenantGuid - The tenant GUID to look up
 * @returns The tenant name or the original GUID if not found
 */
export const getTenantName = (tenantGuid: string): string => {
  return TENANT_ID_MAP[tenantGuid] || tenantGuid;
};

/**
 * Configuration for tenant-specific dialogs
 * Use tenant names as keys instead of GUIDs
 */
export const TENANT_DIALOGS: Record<string, TenantDialogConfig> = {
  horten: {
    title: "Ansvarlig brug af AI",
    description:
      "Som medarbejder i Horten Advokatselskab (herefter ”Horten”) er du underlagt Hortens generelle retningslinjer for brug af AI-værktøjer, herunder følgende:",
    customContent: () => (
      <div className="space-y-4">
        <div className="space-y-2">
          <h4 className="font-semibold text-sm">AI har begrænsninger</h4>
          <p className="text-sm text-muted-foreground">
            AI-værktøjer kan generere fejl, anvende forældede kilder eller
            hallucinere oplysninger. De skal derfor alene benyttes som
            støtteværktøjer. AI erstatter ikke juridisk rådgivning og må ikke
            anvendes som grundlag for beslutningstagning.
          </p>
        </div>

        <div className="space-y-2">
          <h4 className="font-semibold text-sm">Kvalitetssikring</h4>
          <p className="text-sm text-muted-foreground">
            Som medarbejder har du det fulde ansvar for at kvalitetssikre det
            materiale, der opnås fra AI-værktøjet. Kvalitetssikring omfatter
            kildekritik, verificering af kilder, fx referencer til
            domsafgørelser eller udtalelser, og kritisk sproglig gennemgang af
            det producerede materiale, herunder i forhold til den juridiske
            korrekthed.
          </p>
        </div>

        <div className="space-y-2">
          <h4 className="font-semibold text-sm">Indberetning af fejl</h4>
          <p className="text-sm text-muted-foreground">
            Hvis du oplever fejl eller uhensigtsmæssigheder i forbindelse med
            brugen af AI, bedes du indberette dette til{" "}
            <CopyableEmail
              className="text-link"
              email="intern-compliance@horten.dk"
            />
          </p>
        </div>
      </div>
    ),
  },
};

/**
 * Get the dialog configuration for a specific tenant
 * @param tenantGuid - The tenant GUID to get the dialog for
 * @returns The dialog configuration or undefined if not found
 */
export const getTenantDialog = (
  tenantGuid: string
): TenantDialogConfig | undefined => {
  const tenantName = getTenantName(tenantGuid);
  return TENANT_DIALOGS[tenantName];
};
